import { FC, useEffect } from 'react'
import shallow from 'zustand/shallow'
import { ErrorMessage } from '@hookform/error-message'
import { useTranslation } from 'react-i18next'
import { Col, Divider, Checkbox, Tooltip } from 'antd'
import { Control, Controller, useFieldArray, useForm, useWatch, UseFormGetValues, FieldErrors, UseFormSetValue } from 'react-hook-form'
import PerText from '../Text'
import PerSpacer from '../Spacer'
import PerFlexRow from '../FlexRow'
import PerInputNumber from '../PerInputNumber'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { PerProgressBar } from '../ProgressBar'
import { TargetsModalComment } from './TargetsModalComment'
import { useTargetsModalStore } from './TargetsModalStore'
import { TargetsModalAttachment } from './TargetsModalAttachment'
import { TargetsModalCommentInput } from './TargetsModalCommentInput'
import { TargetsModalAsterisk, TargetsModalMessage } from './TargetsModalMessageComponents'
import { ObjectiveStatus, Country, ObjectiveInput } from '../../../types'
import { ObjectiveExtended } from './types'
import './index.less'
import { TFunction } from 'i18next'
import RichTextBox from '../RichTextBox'

const Tasks = ({
  currentObjective,
  control,
  fields,
  getValues,
  //setValue,
  t,
}: {
  currentObjective: ObjectiveInput
  control: Control<ObjectiveInput>
  fields: Record<'id', string>[]
  getValues: UseFormGetValues<ObjectiveInput>
  setValue: UseFormSetValue<ObjectiveInput>
  t: TFunction
}) => {
  useWatch({ control, name: 'keyResults' })

  return (
    <PerFlexRow direction="column">
      <PerSpacer mb="xs">
        <PerProgressBar
          tasksDone={getValues().keyResults.filter(({ completed }) => completed).length}
          tasksAll={currentObjective.keyResults.length}
        />
      </PerSpacer>
      {fields.map((field, index) => {
        return (
          <PerFlexRow mb="s" key={`${field.id}_${index}`} className="fullWidth" align="middle" justify="start" noWrap>
            <Controller
              name={`keyResults.${index}.completed`}
              control={control}
              defaultValue={false}
              render={({ field: { onChange, value } }) => {
                return (
                  <PerFlexRow className="per-formikKeyResultsItem">
                    <Tooltip
                      title={currentObjective.status !== ObjectiveStatus.Closed && t('targetModal.taskCheckboxTooltip')}
                      placement="left"
                    >
                      <Checkbox
                        checked={value}
                        onChange={(e) => {
                          const checked = e.target.checked
                          onChange(checked)
                          if (getValues().keyResults.every((task) => task.completed)) {
                            //setValue('markedAsDone', true)
                          }
                        }}
                        disabled={currentObjective.status === ObjectiveStatus.Closed}
                      >
                        {getValues().keyResults[index].text}
                      </Checkbox>
                    </Tooltip>
                  </PerFlexRow>
                )
              }}
            />
          </PerFlexRow>
        )
      })}
    </PerFlexRow>
  )
}

export const TargetsModalFormEditActiveTarget: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { utcDateStringToMoment, formatDateShort } = useDateTime(user)

  const [currentObjective, currentComments, setNewTargetForm, modalProps] = useTargetsModalStore(
    (state) => [state.currentObjective, state.currentComments, state.setNewTargetForm, state.modalProps],
    shallow,
  )

  const newTargetForm = useForm<ObjectiveExtended>({
    defaultValues: !!currentObjective
      ? {
          ...currentObjective,
          achievedAmountPercentage:
            !!currentObjective.targetAmount && (!!currentObjective.achievedAmount || currentObjective.achievedAmount === 0)
              ? Number(((currentObjective.achievedAmount / currentObjective.targetAmount) * 100).toFixed(2))
              : undefined,
          markedAsDone: !!currentObjective.targetAmount
            ? !!currentObjective.achievedAmount ||
              currentObjective.achievedAmount === 0 ||
              currentObjective.keyResults.every((task) => task.completed)
            : currentObjective.status === ObjectiveStatus.MarkedAsDone,
        }
      : {},
    criteriaMode: 'all',
    mode: 'onChange',
  })

  const {
    control,
    getValues,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = newTargetForm

  const { fields } = useFieldArray<ObjectiveExtended>({
    name: 'keyResults',
    keyName: 'id',
    control,
  })

  watch(['targetAmount', 'keyResults', 'markedAsDone', 'achievedAmount', 'achievedAmountPercentage'])

  const hasError = (errors: FieldErrors<ObjectiveExtended>, fieldName: string) => {
    return !!errors && !!errors[fieldName]
  }

  const formatDate = (date: string) => {
    return formatDateShort(date, true) || '-'
  }

  const calcAchievedAmount = (targetAmount: number | null | undefined, achievedAmountPercentage: number | undefined) => {
    if (!!targetAmount && (!!achievedAmountPercentage || achievedAmountPercentage === 0)) {
      setValue('achievedAmount', Number(((achievedAmountPercentage / 100) * targetAmount).toFixed(2)))
    } else {
      setValue('achievedAmount', undefined)
    }
  }

  const calcAchievedAmountPercentage = (achievedAmount: number | null | undefined, targetAmount: number | null | undefined) => {
    if (!!targetAmount && (!!achievedAmount || achievedAmount === 0)) {
      setValue('achievedAmountPercentage', Number(((achievedAmount / targetAmount) * 100).toFixed(2)))
    } else {
      setValue('achievedAmountPercentage', undefined)
    }
  }

  const handleTargetAmountChange = () => {
    if (!getValues().targetAmount) {
      setValue('achievedAmount', undefined)
      setValue('achievedAmountPercentage', undefined)
    } else {
      if (!!getValues().achievedAmountPercentage) {
        calcAchievedAmount(getValues().targetAmount, getValues().achievedAmountPercentage)
      }
    }
  }

  useEffect(() => {
    setNewTargetForm(newTargetForm)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return !!currentObjective ? (
    <>
      <PerFlexRow direction="column">
        <PerText appearance="label-small" mb="xxs">
          {t('targetModal.title')}
        </PerText>
        <PerText fontWeight="bold">{currentObjective.title}</PerText>
      </PerFlexRow>

      <PerFlexRow mt="xl">
        <PerText appearance="tile-heading">{t('targetModal.tasks')}</PerText>
      </PerFlexRow>

      <Tasks currentObjective={currentObjective} control={control} fields={fields} getValues={getValues} t={t} setValue={setValue} />

      <PerFlexRow mt="l">
        <PerText appearance="tile-heading">{t('targetModal.notes')}</PerText>
      </PerFlexRow>
      <PerFlexRow direction="column" mt="m">
        <Controller
          name="taskNotes"
          control={control}
          render={({ field: { onChange, value } }) => (
            <RichTextBox placeholder={t('targetModal.notesPlaceholder')} text={value || ''} onChange={onChange} />
          )}
        />
      </PerFlexRow>

      <PerFlexRow mt="l">
        <Col span={8}>
          <PerText appearance="label-small" mb="xxs">
            {t('targetModal.startDate')}
          </PerText>
          <PerText>{formatDate(currentObjective.start)}</PerText>
        </Col>
        <Col span={1} />
        <Col span={8}>
          <PerText appearance="label-small" mb="xxs">
            {t('targetModal.dueDate')}
          </PerText>
          <PerText>{formatDate(currentObjective.due)}</PerText>
        </Col>
      </PerFlexRow>

      {!!currentObjective.targetAmount && (
        <>
          <PerFlexRow mt="m">
            <Col span={8}>
              <PerText appearance="label-small" mb="xxs">
                {t('targetModal.targetAmount')}
                {modalProps.employeeCountry && modalProps.employeeCountry === Country.Pol && <TargetsModalAsterisk />}
              </PerText>
              <div style={{ paddingRight: '6px' }}>
                <Controller
                  name="targetAmount"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <PerInputNumber
                      type="currency"
                      value={value as number | undefined}
                      onChange={(value) => {
                        onChange(value)
                        handleTargetAmountChange()
                      }}
                      width="100%"
                      currency={
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        currentObjective.currency!
                      }
                      min={0}
                      timezoneAndLocale={user}
                    />
                  )}
                />
              </div>
            </Col>
          </PerFlexRow>
          <PerFlexRow>
            {modalProps.employeeCountry && modalProps.employeeCountry === Country.Pol && (
              <TargetsModalMessage message={t(`targetModal.targetMessage.${Country.Pol}`)} />
            )}
          </PerFlexRow>
        </>
      )}

      <PerFlexRow mt="xl" mb="l" align="middle">
        <Controller
          name={`markedAsDone`}
          control={control}
          defaultValue={false}
          render={({ field: { onChange, value } }) => (
            <>
              <PerFlexRow className="per-formikKeyResultsItem">
                <Checkbox
                  checked={value}
                  onChange={(e) => {
                    onChange(e)
                    trigger()
                  }}
                  // The mark as done is disabled when all tasks of a monetary targets are checked.
                  // If the existing target is non-monetary, it may already have all tasks checked when the modal opens
                  // and therefore the marked as done would be disabled and unchecked,
                  // which would prevent the dev target from being closed.
                  // Therefore it should not be disabled for dev targets.
                  disabled={!!currentObjective.targetAmount && getValues().keyResults.every((task) => task.completed)}
                >
                  {t('targetModal.markAsDone')}
                </Checkbox>
              </PerFlexRow>
            </>
          )}
        />
      </PerFlexRow>

      {!!currentObjective.targetAmount && (
        <PerFlexRow mt="xs" align="middle">
          <Col span={8}>
            <PerText appearance="label-small" mb="xxs">
              {t('targetModal.achievedAmount')}
            </PerText>
            <Controller
              name="achievedAmount"
              control={control}
              rules={{
                required: { value: !!getValues().markedAsDone, message: `${t('targetModal.achievedAmountRequired')}` },
              }}
              render={({ field: { onChange, value } }) => (
                <>
                  <div className={hasError(errors, 'achievedAmount') ? 'has-error' : ''} style={{ width: '100%' }}>
                    <PerInputNumber
                      type="currency"
                      value={value as number | undefined}
                      onChange={(value) => {
                        onChange(value)
                        calcAchievedAmountPercentage(value, getValues().targetAmount)
                      }}
                      disabled={!getValues().markedAsDone}
                      width={'100%'}
                      currency={
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        currentObjective.currency!
                      }
                      min={0}
                      timezoneAndLocale={user}
                    />
                  </div>
                  <div className="per-target-modal--field-error">
                    <ErrorMessage
                      errors={errors}
                      name="achievedAmount"
                      render={({ messages }) => {
                        return (
                          <>
                            <PerText fontSize="xs" color="error">
                              {!!messages && !!messages.required && messages.required}
                            </PerText>
                          </>
                        )
                      }}
                    />
                  </div>
                </>
              )}
            />
          </Col>
          <Col span={1} />
          <Col span={8}>
            <PerFlexRow mt="m" align="middle">
              <Controller
                name="achievedAmountPercentage"
                control={control}
                rules={{ deps: ['achievedAmount'] }}
                render={({ field: { onChange, value } }) => (
                  <>
                    <div>
                      <PerInputNumber
                        type="percentage"
                        value={value}
                        onChange={(value) => {
                          onChange(value)
                          calcAchievedAmount(getValues().targetAmount, value)
                        }}
                        disabled={!getValues().markedAsDone}
                        width={'100%'}
                        min={0}
                        percentageHasDecimal
                        timezoneAndLocale={user}
                      />
                    </div>
                    <div className="per-target-modal--field-error"></div>
                  </>
                )}
              />
            </PerFlexRow>
          </Col>
        </PerFlexRow>
      )}

      <PerSpacer mt="m" />

      <TargetsModalAttachment showDropZone={true} />

      <Divider />

      <PerFlexRow direction="column">
        <PerText appearance="tile-heading" mb="xxs">
          {t('targetModal.comments')}
        </PerText>
        <PerText fontSize="xs" color="secondary">
          {t('targetModal.commentsReviewerDisclaimer')}
        </PerText>
      </PerFlexRow>

      {currentComments
        .slice()
        .sort((a, b) => {
          const date1 = utcDateStringToMoment(a.date)
          const date2 = utcDateStringToMoment(b.date)
          return date1 && date2 && date1.isBefore(date2) ? -1 : date1 && date2 && date1.isAfter(date2) ? 1 : 0
        })
        .map((note, index) => {
          return <TargetsModalComment comment={note} index={index} key={`${index}${note.date}`} />
        })}

      {!!currentObjective && currentObjective.status !== ObjectiveStatus.Closed && <TargetsModalCommentInput />}
    </>
  ) : null
}
